import {
  SelectOption,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@cmg/design-system';
import {
  FormikAutocompleteField,
  FormikSelectField,
  FormikTextField,
  parseFormikValueToSelectOption,
  parseSelectOptionToFormikValue,
} from '@cmg/design-system-formik';
import React from 'react';

import MandatoryAsterisk from '../../../../design-system/components/data-display/mandatory-asterisk/MandatoryAsterisk';
import { Rank, SellingRestrictions } from '../../../../graphql';

export const SELLING_RESTRICTION_LABELS: Record<SellingRestrictions, string> = {
  [SellingRestrictions.None]: 'None',
  [SellingRestrictions.Reg_144A]: '144A',
};

const SELLING_RESTRICTIONS_OPTIONS = Object.entries(SELLING_RESTRICTION_LABELS).map<
  SelectOption<SellingRestrictions>
>(([value, label]) => ({
  label,
  value: value as SellingRestrictions,
}));

export const RANK_LABELS: Record<Rank, string> = {
  [Rank.Senior]: 'Senior',
  [Rank.Preferred]: 'Preferred',
  [Rank.SeniorSecure]: 'Senior Secure',
  [Rank.Subordinated]: 'Subordinated',
  [Rank.SeniorSubordinated]: 'Senior Subordinated',
};

const RANK_OPTIONS = Object.entries(RANK_LABELS).map<SelectOption<Rank>>(([value, label]) => ({
  label,
  value: value as Rank,
}));

export type DeliveryInstrumentFormProps = Readonly<{
  currencyCodeOptions: SelectOption<string>[];
  countryCodeOptions: SelectOption<string>[];
  exchangeMicOptions: SelectOption<string>[];
  withConvertibleFields: boolean;
  onStockSymbolChange: (stockSymbol: string) => void;
  onCountryCodeChange: (countryCode: string) => void;
  isCountryCodeDisabled?: boolean;
  loading?: boolean;
}>;

export const DeliveryInstrumentForm: React.FC<DeliveryInstrumentFormProps> = ({
  currencyCodeOptions,
  countryCodeOptions,
  exchangeMicOptions,
  withConvertibleFields,
  onStockSymbolChange,
  onCountryCodeChange,
  isCountryCodeDisabled,
  loading,
}) => {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {withConvertibleFields && (
              <React.Fragment>
                <TableCell>Rank</TableCell>
                <TableCell>
                  Selling Restrictions <MandatoryAsterisk />
                </TableCell>
              </React.Fragment>
            )}
            <TableCell width="9%">
              Pricing Currency <MandatoryAsterisk />
            </TableCell>
            <TableCell width="7%">Symbol</TableCell>
            <TableCell width="12%">
              Country <MandatoryAsterisk />
            </TableCell>
            <TableCell width="8%">Exchange</TableCell>
            <TableCell width="12%">CUSIP</TableCell>
            <TableCell width="12%">ISIN</TableCell>
            <TableCell width="12%">FIGI</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {withConvertibleFields && (
              <React.Fragment>
                <TableCell>
                  <FormikAutocompleteField
                    name="deliveryInstrument.rank"
                    options={RANK_OPTIONS}
                    parseFormikValue={parseFormikValueToSelectOption}
                    parseToFormikValue={parseSelectOptionToFormikValue}
                    showHelperTextInTooltip
                    sx={{ minWidth: 140 }}
                  />
                </TableCell>
                <TableCell>
                  <FormikSelectField
                    data-testid="offering-setup-selling-restrictions-field"
                    name="deliveryInstrument.sellingRestrictions"
                    options={SELLING_RESTRICTIONS_OPTIONS}
                    showHelperTextInTooltip
                  />
                </TableCell>
              </React.Fragment>
            )}
            <TableCell>
              <FormikAutocompleteField
                name="deliveryInstrument.currencyCode"
                options={currencyCodeOptions}
                showHelperTextInTooltip
                parseFormikValue={parseFormikValueToSelectOption}
                parseToFormikValue={parseSelectOptionToFormikValue}
                disabled={loading}
              />
            </TableCell>
            <TableCell>
              <FormikTextField
                name="deliveryInstrument.stockSymbol"
                showHelperTextInTooltip
                onChange={onStockSymbolChange}
              />
            </TableCell>
            <TableCell>
              <FormikAutocompleteField
                data-testid="offering-setup-delivery-instrument-country-code-field"
                name="deliveryInstrument.countryCode"
                options={countryCodeOptions}
                showHelperTextInTooltip
                parseFormikValue={parseFormikValueToSelectOption}
                parseToFormikValue={parseSelectOptionToFormikValue}
                onChange={option => onCountryCodeChange(option?.value ?? '')}
                disabled={isCountryCodeDisabled || loading}
              />
            </TableCell>
            <TableCell>
              <FormikAutocompleteField
                data-testid="offering-setup-exchange-mic-field"
                name="deliveryInstrument.exchangeMic"
                options={exchangeMicOptions}
                showHelperTextInTooltip
                parseFormikValue={parseFormikValueToSelectOption}
                parseToFormikValue={parseSelectOptionToFormikValue}
                sx={{ minWidth: 140 }}
              />
            </TableCell>
            <TableCell>
              <FormikTextField name="deliveryInstrument.cusip" showHelperTextInTooltip />
            </TableCell>
            <TableCell>
              <FormikTextField name="deliveryInstrument.isin" showHelperTextInTooltip />
            </TableCell>
            <TableCell>
              <FormikTextField name="deliveryInstrument.figi" showHelperTextInTooltip />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
